import { useCallback, useContext } from "react";
import { SpaceContext } from "@/contexts/space-context";
import { useSettingsAt } from "@/hooks/useTenantPath";
import { useScopedWatch } from "@/hooks/useScopedWatch";
import { settingsAtKey } from "@/domain/tenantPath";
import {
  companyInfoRepo,
  DEFAULT_COMPANY_INFO,
  type CompanyInfo,
  type CompanyInfoSource,
} from "@/services/company_info.repository";

const INITIAL: { info: CompanyInfo; source: CompanyInfoSource } = { info: DEFAULT_COMPANY_INFO, source: "empty" };

/**
 * 회사 기본 정보 구독 — **지금 법인** 것(12 §9-1 ④). 미설정 시 빈 기본값.
 *   - 법인 공간 안: 그 법인 문서(중앙은 없으면 루트 값으로 대체 — source "root")
 *   - 법인 공간 밖(로그인 화면 등, SpaceProvider 없음): 루트 브랜드 문서(로고·그룹명) — 옛 동작 그대로
 * source = 값의 출처(tenant | root | empty). 편집 화면이 "아직 법인 값 없음" 안내에 쓴다.
 */
export function useCompanyInfo(): { info: CompanyInfo; loaded: boolean; source: CompanyInfoSource } {
  const inSpace = useContext(SpaceContext) !== null;
  const at = useSettingsAt();
  const key = inSpace ? settingsAtKey("company_info", at) : "brand";
  const subscribe = useCallback(
    (cb: (v: typeof INITIAL) => void, onError: (e: Error) => void) =>
      inSpace
        ? companyInfoRepo.watch(at, (info, source) => cb({ info, source }), onError)
        : companyInfoRepo.watchBrand((info) => cb({ info, source: "root" }), onError),
    [inSpace, at],
  );
  const { value, loaded } = useScopedWatch(key, subscribe, INITIAL);
  return { info: value.info, loaded, source: value.source };
}

/** 루트 브랜드(로고·그룹명) — 사이드바·로그인 화면. 법인과 무관 */
export function useBrandInfo(): { info: CompanyInfo; loaded: boolean } {
  const { value, loaded } = useScopedWatch("brand", companyInfoRepo.watchBrand, DEFAULT_COMPANY_INFO);
  return { info: value, loaded };
}
